import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import { WeeklyMenuItemRaw } from '../../api/menu';
import { MenuItemDto } from '../../api-types';
import { FoodIcon } from '../foodIcon';
import { MenuItemPopup } from '../menuItemPopup';
import { modalStyles } from '../../config/modal';
import { capitalizeFirstLetter } from '../../utils/capitalizeCase';

import './index.css';

const MAX_VISIBLE_ALLERGENS = 3;

function toMenuItemDto(item: WeeklyMenuItemRaw): MenuItemDto {
    return item as unknown as MenuItemDto;
}

interface IProps {
    item: WeeklyMenuItemRaw;
    hideImage?: boolean;
}

export function WeeklyMenuItem({ item, hideImage = false }: Readonly<IProps>) {
    const { t } = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const displayPrice =
        item.discountedPrice != null
            ? item.discountedPrice
            : item.subsidizedPrice;

    const visibleAllergens = item.allergens.slice(0, MAX_VISIBLE_ALLERGENS);
    const extraCount = item.allergens.length - MAX_VISIBLE_ALLERGENS;

    return (
        <>
            <div
                className={`wmi-card${hideImage ? ' wmi-card--compact' : ''}`}
                onClick={() => setIsModalOpen(true)}
                role='button'
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setIsModalOpen(true)}
            >
                <div
                    className={`wmi-icon-wrap wmi-icon-wrap--${item.icon.toLowerCase()}`}
                >
                    <FoodIcon
                        foodType={item.icon}
                        color={item.icon.toLowerCase()}
                    />
                </div>

                <div className='wmi-content'>
                    <div className='wmi-title-row'>
                        <span className='wmi-title'>{item.title}</span>
                        {!hideImage && (
                            <span className='wmi-price-wrap'>
                                {displayPrice != null ? (
                                    <>
                                        <span className='wmi-price-strike'>
                                            {item.fullPrice.toFixed(2)} €
                                        </span>
                                        <span className='wmi-price wmi-price--sale'>
                                            {displayPrice.toFixed(2)} €
                                        </span>
                                    </>
                                ) : (
                                    <span className='wmi-price'>
                                        {item.fullPrice.toFixed(2)} €
                                    </span>
                                )}
                            </span>
                        )}
                    </div>

                    {item.description && (
                        <p className='wmi-desc'>{item.description}</p>
                    )}

                    {item.allergens.length > 0 && (
                        <div className='wmi-allergens'>
                            {visibleAllergens.map((a) => (
                                <span key={a} className='wmi-allergen'>
                                    {capitalizeFirstLetter(a.toLowerCase())}
                                </span>
                            ))}
                            {extraCount > 0 && (
                                <span className='wmi-allergen wmi-allergen--more'>
                                    +{extraCount} {t('more') ?? 'more'}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {!hideImage && item.image && (
                    <img
                        className='wmi-thumb'
                        src={item.image}
                        alt={item.title}
                    />
                )}

                {hideImage && (
                    <div className='wmi-price-right'>
                        {displayPrice != null ? (
                            <>
                                <span className='wmi-price-strike'>
                                    {item.fullPrice.toFixed(2)} €
                                </span>
                                <span className='wmi-price wmi-price--sale'>
                                    {displayPrice.toFixed(2)} €
                                </span>
                            </>
                        ) : (
                            <span className='wmi-price'>
                                {item.fullPrice.toFixed(2)} €
                            </span>
                        )}
                    </div>
                )}
            </div>

            {isModalOpen && (
                <Modal
                    isOpen={isModalOpen}
                    style={modalStyles}
                    onRequestClose={() => setIsModalOpen(false)}
                    ariaHideApp={false}
                >
                    <MenuItemPopup
                        menuItem={toMenuItemDto(item)}
                        setIsModalOpen={setIsModalOpen}
                    />
                </Modal>
            )}
        </>
    );
}
